import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api, { videoAPI } from '@/utils/api';
import { NEXT_ACTION, getVideoNextAction } from './videoWorkflow';

/**
 * Round 76 — SHARED React hook + component for the PayGBot video
 * production workflow. Used by EVERY video page (Create Content,
 * Weekly Planner, Stories, Run My Week, My Projects).
 *
 * Encapsulates:
 *  - voice preview request (ElevenLabs via shared /api/videos/voiceover-preview)
 *  - voice preview persistence callback (page-specific)
 *  - video generate request (page-specific via onGenerateVideo)
 *  - video retry request (page-specific via onRetryVideo)
 *  - rendering the correct CTA for each next_action state
 */

const NEXT_ACTION_BUTTON_LABEL = {
  [NEXT_ACTION.NEEDS_SCRIPT]: 'Add script first',
  [NEXT_ACTION.NEEDS_VOICE_PREVIEW]: 'Preview voiceover',
  [NEXT_ACTION.READY_TO_GENERATE_VIDEO]: 'Generate Video',
  [NEXT_ACTION.GENERATING_VIDEO]: 'Generating video…',
  [NEXT_ACTION.VIDEO_FAILED]: 'Retry Video',
  [NEXT_ACTION.VIDEO_READY]: 'Video ready · finish setup',
  [NEXT_ACTION.READY_TO_APPROVE]: 'Approve / schedule',
};

/**
 * useVideoWorkflow — wraps the shared workflow state so any video
 * page can drop in the standard sequence.
 *
 * @param {object} params
 * @param {object} params.asset — asset dict that compute_next_action understands
 * @param {(voicePreview: object) => Promise<void>} params.onPersistVoicePreview
 *        Called when ElevenLabs preview succeeds. Caller persists to
 *        their own DB shape (planner post, draft, generated_content).
 * @param {() => Promise<void>} params.onGenerateVideo
 *        Caller fires the video generate API for their surface.
 * @param {() => Promise<void>} params.onRetryVideo
 *        Caller fires the retry endpoint for their surface.
 * @param {() => Promise<void>} [params.onApprove] — optional
 */
export const useVideoWorkflow = ({
  asset,
  onPersistVoicePreview,
  onGenerateVideo,
  onRetryVideo,
  onApprove,
}) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const workflow = useMemo(() => getVideoNextAction(asset || {}), [asset]);

  const previewVoiceover = useCallback(async () => {
    const cleanScript = (asset?.script || asset?.voiceover_script || '').trim();
    if (!cleanScript) {
      const msg = 'Script is required to preview the voiceover.';
      setPreviewError(msg);
      toast.error(msg);
      return null;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const resp = await videoAPI.previewVoiceover({ script: cleanScript });
      const voicePreview = {
        audio_url: resp.audio_url,
        audio_duration: resp.audio_duration,
        script_hash: resp.script_hash,
        cached: Boolean(resp.cached),
        previewed_at: new Date().toISOString(),
      };
      // Page-specific persistence (PUT /weekly-content/post, draft state, etc.)
      if (onPersistVoicePreview) {
        try {
          await onPersistVoicePreview(voicePreview);
        } catch (persistErr) {
          // Surface as a soft warning — user can still hear the preview.
          console.warn('[VideoWorkflow] voice_preview persistence failed:', persistErr);
        }
      }
      toast.success(
        resp.cached
          ? `Using cached voiceover (free) — ${(resp.audio_duration || 0).toFixed(1)}s.`
          : `Voiceover preview ready — ${(resp.audio_duration || 0).toFixed(1)}s.`,
      );
      return voicePreview;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : err?.message || 'Voiceover preview failed.';
      setPreviewError(msg);
      toast.error(msg);
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [asset, onPersistVoicePreview]);

  const triggerGenerate = useCallback(async () => {
    if (generateLoading) return;
    setGenerateLoading(true);
    try {
      if (workflow.next_action === NEXT_ACTION.VIDEO_FAILED) {
        if (onRetryVideo) await onRetryVideo();
      } else if (workflow.next_action === NEXT_ACTION.READY_TO_GENERATE_VIDEO) {
        if (onGenerateVideo) await onGenerateVideo();
      } else if (workflow.next_action === NEXT_ACTION.NEEDS_VOICE_PREVIEW) {
        await previewVoiceover();
      }
    } finally {
      setGenerateLoading(false);
    }
  }, [generateLoading, workflow.next_action, onRetryVideo, onGenerateVideo, previewVoiceover]);

  return {
    workflow,
    previewLoading,
    previewError,
    generateLoading,
    previewVoiceover,
    triggerGenerate,
    approve: onApprove,
  };
};

/**
 * VideoWorkflowActions — single rendered button(s) for whatever the
 * shared workflow says the user must do next. ALL video pages
 * render this. No page is allowed to render a custom Generate Video
 * button.
 */
export const VideoWorkflowActions = ({
  asset,
  onPersistVoicePreview,
  onGenerateVideo,
  onRetryVideo,
  onApprove,
  testIdPrefix = 'video-workflow',
  isInternalAccount = false,
}) => {
  const { workflow, previewLoading, generateLoading, previewVoiceover, triggerGenerate } =
    useVideoWorkflow({
      asset, onPersistVoicePreview, onGenerateVideo, onRetryVideo, onApprove,
    });

  const action = workflow.next_action;
  const label = NEXT_ACTION_BUTTON_LABEL[action] || 'Next';
  const inFlight = action === NEXT_ACTION.GENERATING_VIDEO || generateLoading || previewLoading;

  // Phase D (2026-05-27) — Weekly Planner generate/retry now routes
  // to v2 (`/generate-video-v2`, `/retry-video-v2`) for ALL users.
  // The Phase B null-return that hid these buttons from clients is
  // removed because clients are now eligible. The backend
  // `_require_video_billing_ok` gate refuses without provider calls
  // if the script is over the standard tier or the upcharge isn't
  // confirmed. `isInternalAccount` is kept as a prop for future
  // founder-only diagnostic surfaces.

  const variantClass = {
    [NEXT_ACTION.NEEDS_SCRIPT]: 'bg-slate-700/60 text-slate-400 cursor-not-allowed border border-slate-600/40',
    [NEXT_ACTION.NEEDS_VOICE_PREVIEW]: 'bg-violet-600 hover:bg-violet-500 text-white shadow-md shadow-violet-500/20',
    [NEXT_ACTION.READY_TO_GENERATE_VIDEO]: 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white shadow-md shadow-fuchsia-500/25',
    [NEXT_ACTION.GENERATING_VIDEO]: 'bg-slate-700/50 text-slate-300 cursor-not-allowed border border-slate-600/50',
    [NEXT_ACTION.VIDEO_FAILED]: 'bg-red-500 hover:bg-red-600 text-white',
    [NEXT_ACTION.VIDEO_READY]: 'bg-amber-500/20 text-amber-300 border border-amber-500/40 cursor-default',
    [NEXT_ACTION.READY_TO_APPROVE]: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  }[action] || 'bg-slate-700 text-slate-300';

  const disabled =
    inFlight ||
    action === NEXT_ACTION.NEEDS_SCRIPT ||
    action === NEXT_ACTION.GENERATING_VIDEO ||
    action === NEXT_ACTION.VIDEO_READY;

  const handleClick = async () => {
    if (disabled) return;
    if (action === NEXT_ACTION.NEEDS_VOICE_PREVIEW) {
      await previewVoiceover();
      return;
    }
    if (action === NEXT_ACTION.READY_TO_APPROVE) {
      if (onApprove) await onApprove();
      return;
    }
    await triggerGenerate();
  };

  const tooltip = workflow.reasons?.join(' • ') || '';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={tooltip}
      data-testid={`${testIdPrefix}-${action}`}
      data-next-action={action}
      className={`w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${variantClass}`}
    >
      {inFlight ? (
        <>
          <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
          <span>{label}</span>
        </>
      ) : (
        <span>{label}</span>
      )}
    </button>
  );
};
